import React, {
  ComponentType,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { Dayjs } from "dayjs";
import { ChevronDown, ChevronUp, Filter } from "react-feather";

import KpiCardJs from "../../shared/Dashboard/KpiCard/KpiCard";
import DatePickerJs from "../../shared/DatePicker/DatePicker";
import Button from "../../shared/Button/Button";
import ExportToXlsxButtonJs from "../../CommonComponents/ExportToXlsxButton/ExportToXlsxButton";

import DailyPerformance from "./DailyPerformance/DailyPerformance";
import TopFiveChart from "./TopFiveChart/TopFiveChart";
import SportsbookByState from "./SportsbookByState/SportsbookByState";
import SportsbookKpiFiltersSection, {
  EMPTY_SPORTSBOOK_KPI_FILTERS,
} from "./SportsbookKpiFiltersSection/SportsbookKpiFiltersSection";

import { DATE_RANGE, KPI_CARD_BACKGROUND } from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { getAdminNow } from "../../../utils/dateService";
import { useGetSportsbookKpiCards, CARD_ORDER } from "../../../queries/sportsbook-kpis/useGetSportsbookKpiCards";
import { useGetSportsbookTopSports } from "../../../queries/sportsbook-kpis/useGetSportsbookTopSports";
import { useGetSportsbookTopLeagues } from "../../../queries/sportsbook-kpis/useGetSportsbookTopLeagues";

import styles from "./SportsbookKPIsV2.module.css";
import { useKpiTabToolbar } from "../KpiTabToolbarContext";
import { useClientCountry } from "../../../hooks/useClientCountry";
import {
  defaultDateRangeForViewport,
  defaultStartRangeForViewport,
} from "../../../utils/narrowViewportChartDates";
import {
  createDefaultSportsbookKpiFilterOptions,
  type SportsbookKpiFilterValues,
} from "../../../queries/sportsbook-kpis/sportsbookKpiFilters";

const KpiCard = KpiCardJs as ComponentType<any>;
const CustomDatePicker = DatePickerJs as ComponentType<any>;
const ExportToXlsxButton = ExportToXlsxButtonJs as ComponentType<any>;

type DateRange = [Dayjs, Dayjs];

// Count metrics render as whole numbers; the rest keep 2 decimals.
const COUNT_KEYS = new Set(["total_bets", "total_users"]);

interface SportsbookKPIsV2Props {
  currencySymbol?: string;
}

const SportsbookKPIsV2: React.FC<SportsbookKPIsV2Props> = ({
  currencySymbol,
}) => {
  // TCM-driven country code → drives the SportsbookByState geo map.
  const clientCountry = useClientCountry();

  const [dates, setDates] = useState<DateRange>(() =>
    defaultDateRangeForViewport(() => {
      const now = getAdminNow();
      return [now.startOf("month"), now.endOf("day")];
    }),
  );
  const [startRange] = useState(() =>
    defaultStartRangeForViewport(DATE_RANGE.CURRENT_MONTH),
  );
  const [appliedFilters, setAppliedFilters] = useState<
    Required<SportsbookKpiFilterValues>
  >(EMPTY_SPORTSBOOK_KPI_FILTERS);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const seededFilters = useRef(false);

  // Labels live inside render so they pick up strings.json reassignment.
  const kpiLabels: Record<string, string> = {
    turnover: AppStrings.TURNOVER,
    pnl: AppStrings.PROFIT_LOSS,
    margin_pct: AppStrings.MARGIN_PCT,
    total_bets: AppStrings.TOTAL_BETS,
    avg_bet_amount: AppStrings.AVG_BET_AMOUNT,
    total_users: AppStrings.TOTAL_ACTIVE_USERS,
  };

  // Date range drives the query keys, so changing it triggers a refetch.
  const queryFilters = useMemo(
    () => ({
      start_date: dates[0].format("YYYY-MM-DD"),
      end_date: dates[1].format("YYYY-MM-DD"),
      ...appliedFilters,
    }),
    [appliedFilters, dates],
  );

  const {
    data: kpiResult,
    isFetching: isCardsLoading,
    isError,
  } = useGetSportsbookKpiCards(queryFilters);
  const kpiCards = kpiResult?.cards ?? [];
  const defaultFilterOptions = useMemo(
    () => createDefaultSportsbookKpiFilterOptions(),
    [],
  );
  const filterOptions =
    kpiResult?.filterOptions ?? defaultFilterOptions;

  useEffect(() => {
    if (seededFilters.current || !kpiResult?.selectedFilters) return;
    seededFilters.current = true;
    setAppliedFilters({
      ...kpiResult.selectedFilters,
      bet_type: [],
      money_type: [],
    });
  }, [kpiResult?.selectedFilters]);

  const getData = useCallback((_queryString: string | null) => {
    // Date changes flow through `dates` -> query keys, which refetches.
  }, []);

  const exportRows = useMemo(
    () =>
      kpiCards.map((item) => ({
        metric: kpiLabels[item.key] ?? item.key,
        value: item.value,
        percent_change: item.percentage,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kpiCards],
  );

  // Portal date picker + export into the shared KPI tabs row so the
  // controls wrap below the tab strip on narrow viewports rather than
  // overlap it (the absolute-positioned header used to do this).
  const tabToolbar = useMemo(
    () => (
      <>
        <Button
          onClick={() => setFiltersVisible((visible) => !visible)}
          aria-expanded={filtersVisible}
          aria-label={AppStrings.PAM_REPORT_FILTERS}
        >
          <Filter size={18} aria-hidden="true" />
          {filtersVisible ? (
            <ChevronUp size={16} aria-hidden="true" />
          ) : (
            <ChevronDown size={16} aria-hidden="true" />
          )}
        </Button>
        <CustomDatePicker
          getData={getData}
          dates={dates}
          setDates={setDates}
          showPresets={true}
          startRange={startRange}
        />
        <ExportToXlsxButton
          data={exportRows}
          filename="sportsbook-kpis.xlsx"
        />
      </>
    ),
    [
      dates,
      exportRows,
      filtersVisible,
      getData,
      startRange,
    ],
  );

  useKpiTabToolbar(tabToolbar);

  return (
    <div className={styles.sportsbookV2}>
      {isError && (
        <div className={styles.errorBanner} data-testid="sportsbook-kpi-error">
          {AppStrings.SPORTSBOOK_KPI_CARDS_ERROR}
        </div>
      )}

      {filtersVisible && (
        <SportsbookKpiFiltersSection
          value={appliedFilters}
          options={filterOptions}
          betTypeAvailable={Boolean(
            kpiResult?.availableFilters?.bet_type,
          )}
          loading={isCardsLoading}
          onApply={setAppliedFilters}
        />
      )}

      <div className={styles.kpiGrid} data-testid="sportsbook-kpi-grid">
        {kpiCards.length === 0 && isCardsLoading
          ? CARD_ORDER.map((key) => (
              <KpiCard
                key={key}
                isApiRunning={true}
                title={kpiLabels[key] ?? key}
                value={0}
                percentage={0}
                rate={false}
                currencySymbol={currencySymbol}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
              />
            ))
          : kpiCards.map((item) => (
              <KpiCard
                key={item.key}
                isApiRunning={isCardsLoading}
                title={kpiLabels[item.key] ?? item.key}
                value={item.value}
                percentage={item.percentage}
                rate={item.rate}
                maximumFractionDigits={
                  item.rate ? 2 : COUNT_KEYS.has(item.key) ? 0 : 2
                }
                currencySymbol={currencySymbol}
                hugeValue={false}
                backgroundColor={KPI_CARD_BACKGROUND.DARK}
              />
            ))}
      </div>

      <DailyPerformance />

      <div className={styles.topRankingRow}>
        <TopFiveChart
          title={AppStrings.SPORTSBOOK_TOP_SPORTS}
          exportFilename="sportsbook-top-sports.xlsx"
          useData={useGetSportsbookTopSports}
        />
        <TopFiveChart
          title={AppStrings.SPORTSBOOK_TOP_LEAGUES}
          exportFilename="sportsbook-top-leagues.xlsx"
          useData={useGetSportsbookTopLeagues}
        />
      </div>

      <SportsbookByState country={clientCountry} />
    </div>
  );
};

export default SportsbookKPIsV2;
